<template>
  <div class="integration-monitoring-table">
    <v-data-table
      id="IntegrationMonitoringTable"
      class="default-table-height elevation-1"
      item-key="batchId"
      show-expand
      single-expand
      :headers="headers"
      :items="records"
      :loading="loadingTable"
      :items-per-page="pagination.itemsPerPage"
      :server-items-length="pagination.totalItems"
      :page="pagination.page"
      :expanded.sync="expanded"
      :no-data-text="$t('integrationMonitoring.table.noData')"
      :loading-text="$t('integrationMonitoring.table.loading')"
      :no-result-text="$t('integrationMonitoring.table.noResult')"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 25, 50, 100],
        disablePagination: loadingTable,
      }"
      @update:options="onUpdateOptions"
    >
      <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
        <v-icon
          v-if="canExpand(item)"
          class="v-data-table__expand-icon integration-monitoring-table__expand-icon"
          :class="{ 'v-data-table__expand-icon--active': isExpanded }"
          size="16"
          @click.stop="expand(!isExpanded)"
        >
          $expand
        </v-icon>
      </template>

      <template v-slot:[`item.carrier`]="{ item }">
        {{ item.carrier || '—' }}
      </template>

      <template v-slot:[`item.financialGroup`]="{ item }">
        {{ item.financialGroup || '—' }}
      </template>

      <template v-slot:[`item.contract`]="{ item }">
        {{ item.contract || '—' }}
      </template>

      <template v-slot:[`item.competence`]="{ item }">
        {{ formatCompetence(item.competence) }}
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <span
          class="im-status-badge"
          :class="`im-status-badge--${getStatusVisual(item.status).tone}`"
        >
          <span class="im-status-badge__dot" />
          <span class="im-status-badge__label">
            {{ getStatusLabel(item.status) }}
          </span>
        </span>
      </template>

      <template v-slot:[`item.receivedAt`]="{ item }">
        <span class="integration-monitoring-table__received-at">
          {{ formatReceivedAt(item.receivedAt) }}
        </span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <div class="integration-monitoring-table__actions">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                icon
                color="primary"
                :loading="Boolean(exportingById[item.batchId])"
                :disabled="loadingTable"
                @click="onExport(item.batchId)"
              >
                <v-icon>mdi-tray-arrow-down</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('integrationMonitoring.actions.exportJson') }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:expanded-item="{ headers: tableHeaders, item }">
        <td
          :colspan="tableHeaders.length"
          class="pa-0"
        >
          <div class="integration-monitoring-table__error-message">
            <v-icon
              color="error"
              size="18"
            >
              mdi-alert-circle-outline
            </v-icon>
            <span>{{ getExpandedErrorText(item) }}</span>
          </div>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import Formatters from '@/shared/formatters/formatters';
import { REQUEST_STATUS_VISUAL_MAP } from '@/store/modules/integration-monitoring/integration-monitoring-config';

const DEFAULT_STATUS_VISUAL = { tone: 'info' };

export default {
  name: 'IntegrationMonitoringTable',
  data() {
    return {
      expanded: [],
      formatter: new Formatters(),
    };
  },
  computed: {
    ...mapGetters({
      records: 'integration-monitoring/records',
      loadingTable: 'integration-monitoring/loadingTable',
      pagination: 'integration-monitoring/pagination',
      exportingById: 'integration-monitoring/exportingById',
    }),
    headers() {
      return [
        {
          text: '',
          value: 'data-table-expand',
          sortable: false,
          width: '48px',
        },
        {
          text: this.$t('integrationMonitoring.table.headers.carrier'),
          value: 'carrier',
          sortable: false,
        },
        {
          text: this.$t('integrationMonitoring.table.headers.financialGroup'),
          value: 'financialGroup',
          sortable: false,
        },
        {
          text: this.$t('integrationMonitoring.table.headers.contract'),
          value: 'contract',
          sortable: false,
        },
        {
          text: this.$t('integrationMonitoring.table.headers.competence'),
          value: 'competence',
          sortable: false,
        },
        {
          text: this.$t('integrationMonitoring.table.headers.status'),
          value: 'status',
          sortable: false,
          width: '220px',
          class: 'integration-monitoring-table__col-status',
          cellClass: 'integration-monitoring-table__col-status',
        },
        {
          text: this.$t('integrationMonitoring.table.headers.receivedAt'),
          value: 'receivedAt',
          sortable: false,
          width: '168px',
          class: 'integration-monitoring-table__col-received-at',
          cellClass: 'integration-monitoring-table__col-received-at',
        },
        {
          text: this.$t('integrationMonitoring.table.headers.actions'),
          value: 'actions',
          sortable: false,
          align: 'end',
          width: '88px',
        },
      ];
    },
  },
  watch: {
    records() {
      this.expanded = [];
    },
  },
  methods: {
    ...mapActions({
      updateTableOptions: 'integration-monitoring/updateTableOptions',
      exportBatch: 'integration-monitoring/exportBatch',
    }),
    getStatusVisual(status) {
      if (!status) return DEFAULT_STATUS_VISUAL;
      return REQUEST_STATUS_VISUAL_MAP[status] || DEFAULT_STATUS_VISUAL;
    },
    getStatusLabel(status) {
      if (!status) return '—';
      const key = `integrationMonitoring.filter.requestStatus.${status}`;
      const translated = this.$t(key);
      return translated === key ? status : translated;
    },
    canExpand(item) {
      if (!item) return false;
      if (item.errorMessage) return true;
      if (item.status === 'COMPLETED_WITH_ERRORS') return true;
      return Number(item.errorItems) > 0;
    },
    getExpandedErrorText(item) {
      if (item?.errorMessage) return item.errorMessage;
      const count = Number(item?.errorItems) || 0;
      return this.$t('integrationMonitoring.table.batchErrorItems', { n: count });
    },
    formatCompetence(competence) {
      if (!competence) return '—';
      const parsed = moment(competence, ['YYYY-MM', 'YYYY-MM-DD', moment.ISO_8601], true);
      if (!parsed.isValid()) return competence;
      return parsed.format('MM/YYYY');
    },
    formatReceivedAt(receivedAt) {
      if (!receivedAt) return '—';
      return this.formatter.formatDateTimeZoneWithHours(receivedAt) || '—';
    },
    async onUpdateOptions({ page, itemsPerPage }) {
      if (!page || !itemsPerPage || itemsPerPage < 0) return;

      await this.updateTableOptions({ page, itemsPerPage });
    },
    async onExport(batchId) {
      await this.exportBatch(batchId);
    },
  },
};
</script>

<style lang="scss">
@import "@/shared/styles/integrationMonitoring/table.scss";
</style>
